import { isEmpty, pipe } from "ramda";
import { createSelector } from "reselect";

import { sortCompanies } from "../helpers";
import { constructOutboundUrl } from "../helpers/constructOutboundUrl";
import {
	filterAdNetworkCompanies,
	filterEligibleAndGeoRelevantCompanies,
} from "../helpers/filters";
import { logError } from "../helpers/logError";
import {
	getCompanyPositionOverride,
	getCompanyVisitPath,
	getContentfulCompanies,
	getFilter,
	getFilteringParameters,
	getIsMobileTargetedPath,
	getIsTargetedPath,
	getMediaAlphaMinimumBid,
	getSortCompaniesByMediaAlphaBid,
	getStateForSentryExceptions,
	getUserGeo,
	getVertical,
	getVerticalFilterFunctions,
} from "./index";
import {
	getIsMediaAlphaTargetedPath,
	getMediaAlphaCompanies,
	processMediaAlphaCompanies,
} from "./mediaAlpha";

const getUnfilteredCompanies = createSelector(
	[getContentfulCompanies, getMediaAlphaCompanies, getMediaAlphaMinimumBid],
	(contentfulCompanies, mediaAlphaCompanies, mediaAlphaMinimumBid) => {
		if (mediaAlphaCompanies) {
			return processMediaAlphaCompanies(
				contentfulCompanies,
				mediaAlphaCompanies,
				mediaAlphaMinimumBid,
			);
		}

		return filterAdNetworkCompanies(contentfulCompanies);
	},
);

// has visibility for testing purposes
export const companiesSelector = createSelector(
	[
		getUnfilteredCompanies,
		getContentfulCompanies,
		getUserGeo,
		getIsTargetedPath,
		getVertical,
		getIsMediaAlphaTargetedPath,
		getStateForSentryExceptions,
		getFilteringParameters,
		getVerticalFilterFunctions,
	],
	(
		unfilteredCompanies,
		contentfulCompanies,
		userGeo,
		isTargetedPath,
		vertical,
		isMediaAlphaTargetedPath,
		sentryExceptionsState,
		filteringParameters,
		filterFunctions,
	) => {
		// apply universal filters (targeted, geo, legacy media alpha)
		const defaultFilteredCompanies = filterEligibleAndGeoRelevantCompanies(
			unfilteredCompanies,
			userGeo,
			isMediaAlphaTargetedPath,
		);
		const companies = isTargetedPath
			? defaultFilteredCompanies
			: unfilteredCompanies;

		// Apply filters configured in filterFunctions using the filteringParameters
		// Note: Age-based filtering (over 55) for life insurance uses a different mechanism
		// via rating key switching (rating vs overAge50Rating) in sortCompanies function
		let companiesToRender = companies;
		for (const [key, value] of Object.entries(filteringParameters)) {
			const filterFn = filterFunctions[key];

			if (filterFn) {
				companiesToRender = filterFn(value, companiesToRender);
			}
		}

		const shouldRenderFallbackCompanies = isEmpty(companiesToRender);
		if (shouldRenderFallbackCompanies) {
			return getFallbackCompanies(
				contentfulCompanies,
				vertical,
				sentryExceptionsState,
			);
		} else return companiesToRender;
	},
);

export const getFallbackCompanies = (contentfulCompanies, vertical) => {
	if (!vertical)
		throw new Error(
			"vertical not passed to ensureNonZeroCompanyCount helper function",
		);
	if (!contentfulCompanies)
		throw new Error(
			`No Contentful companies associated with vertical: ${vertical}. Every vertical needs at least 1 company`,
		);

	const fallbackCompaniesFilter = pipe(
		filterEligibleAndGeoRelevantCompanies, // company must be recommended and permitted in user's geo
		filterAdNetworkCompanies, // filter out unmatched ad network companies
	);
	const fallbackCompanies = fallbackCompaniesFilter(contentfulCompanies);
	if (fallbackCompanies.length === 0) {
		throw new Error(
			`Unable to render fallback companies. No eligible fallback companies for vertical: ${vertical}. There must be a recommended company with an organic partnership for every vertical.`,
		);
	} else {
		logError(
			`Fallback companies rendered for vertical: ${vertical}. This may have occurred for various reasons (user applied filtering, no MA company matches in payload, etc...)`,
		);
		return fallbackCompanies;
	}
};
export const sortedCompaniesSelector = createSelector(
	[
		getVertical,
		getFilter,
		getFilteringParameters,
		companiesSelector,
		getIsMobileTargetedPath,
		getSortCompaniesByMediaAlphaBid,
		getCompanyPositionOverride,
	],
	(
		vertical,
		filter,
		filteringInformation,
		companies,
		isMobileTargetedPath,
		sortCompaniesByMediaAlphaBid,
		companyPositionOverride,
	) =>
		sortCompanies(
			vertical,
			filter,
			filteringInformation,
			companies,
			isMobileTargetedPath,
			sortCompaniesByMediaAlphaBid,
			companyPositionOverride,
		),
);

export const getTopCompany = createSelector(
	[sortedCompaniesSelector],
	(sortedCompanies) => sortedCompanies?.[0],
);

export const getTopCompanyVisitLink = createSelector(
	[(state) => state, getTopCompany],
	(state, topCompany) => {
		const path = getCompanyVisitPath(state, {
			company: topCompany,
			companyIndex: 0,
		});
		return constructOutboundUrl(path);
	},
);
